<template>
  <div class="employee-page">
    <div class="employee-shell" v-loading="editLoading">
      <header class="page-header">
        <div>
          <h1>
            {{ isEditMode ? "编辑普工入职信息" : "普工入职信息收集系统" }}
          </h1>
          <p>
            {{
              isEditMode
                ? "修改已有入职资料，带 * 号为必填项"
                : "请如实填写个人信息，带 * 号为必填项"
            }}
          </p>
        </div>
        <div class="header-buttons">
          <el-button
            v-if="isEditMode"
            class="header-back-button"
            icon="el-icon-arrow-left"
            @click="goBackToDetail"
          >
            返回详情
          </el-button>
        </div>
      </header>

      <div class="step-wrapper">
        <el-steps :active="activeStep" finish-status="success" align-center>
          <el-step
            v-for="(item, index) in steps"
            :key="item.title"
            :title="item.title"
            :icon="item.icon"
            class="clickable-step"
            @click.native="goToStep(index)"
          />
        </el-steps>
      </div>

      <el-form
        ref="employeeForm"
        :model="form"
        :rules="rules"
        label-position="top"
        class="employee-form"
      >
        <section v-show="activeStep === 0" class="step-content">
          <div class="section-title">
            <i class="el-icon-user-solid"></i>
            <span>个人基础信息</span>
          </div>

          <div class="form-grid">
            <el-form-item label="姓名" prop="name">
              <el-input v-model.trim="form.name" placeholder="请输入姓名" />
            </el-form-item>
            <el-form-item label="性别" prop="gender">
              <el-select
                v-model="form.gender"
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in genderOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="出生日期" prop="birthDate">
              <mobile-date-picker
                v-model="form.birthDate"
                placeholder="请选择出生日期"
              />
            </el-form-item>
            <el-form-item label="民族" prop="nation">
              <el-select
                v-model="form.nation"
                filterable
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in nationOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="籍贯" prop="nativePlace">
              <el-cascader
                v-model="form.nativePlace"
                :options="provinceAndCityData"
                placeholder="请选择省/市"
                clearable
              />
            </el-form-item>
            <el-form-item label="户籍性质" prop="householdType">
              <el-select
                v-model="form.householdType"
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in householdTypeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="婚姻状况" prop="maritalStatus">
              <el-select
                v-model="form.maritalStatus"
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in maritalStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="政治面貌" prop="politicalStatus">
              <el-select
                v-model="form.politicalStatus"
                clearable
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in politicalStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="身份证号" prop="idCard">
              <el-input
                ref="idCardInput"
                v-model.trim="form.idCard"
                maxlength="18"
                :readonly="isMobileDevice"
                placeholder="请输入18位身份证号"
                @input="onPcIdCardInput"
                @click.native.stop="onIdCardFieldClick"
              />
            </el-form-item>
            <!-- 身份证专用数字键盘：仅移动端使用（Vant 键盘桌面端鼠标兼容性差） -->
            <van-number-keyboard
              v-if="isMobileDevice"
              :show="idCardKeyboardShow"
              :z-index="3000"
              extra-key="X"
              close-button-text="完成"
              @input="onIdCardKeyInput"
              @delete="onIdCardKeyDelete"
              @blur="onIdCardKeyboardBlur"
            />
            <!-- 省市区选择：手机底部弹出三级联动，体验优于 PC 端级联下拉 -->
            <van-popup v-model="areaPickerVisible" position="bottom" :lazy-render="false">
              <van-area
                title="请选择省市区"
                :area-list="areaList"
                :value="currentAreaCode"
                @confirm="onAreaConfirm"
                @cancel="areaPickerVisible = false"
              />
            </van-popup>
            <el-form-item label="身份证有效期" prop="idCardExpiry">
              <mobile-date-picker
                v-model="form.idCardExpiry"
                placeholder="请选择有效期"
                show-long-term
                long-term-value="9999-01-01"
                long-term-label="长期"
              />
            </el-form-item>

            <el-form-item label="身份证签发机构" prop="idCardAuthority">
              <el-input v-model.trim="form.idCardAuthority" placeholder="请输入签发机构" />
            </el-form-item>
            <el-form-item label="联系手机号" prop="phone">
              <el-input
                v-model.trim="form.phone"
                maxlength="11"
                placeholder="请输入11位手机号"
              />
            </el-form-item>

            <el-form-item label="身份证地址" prop="idCardAddress" class="full-row">
              <div class="address-row">
                <div class="address-area" @click="openAreaPicker('idCard')">
                  <span :class="{ 'is-placeholder': !form.idCardAreaText }">{{
                    form.idCardAreaText || "请选择省/市/区"
                  }}</span>
                  <i class="el-icon-arrow-down address-area__icon"></i>
                </div>
                <el-input
                  v-model.trim="form.idCardAddress"
                  placeholder="请填写详细地址（门牌号等）"
                  class="address-detail"
                />
              </div>
            </el-form-item>
            <el-form-item label="现住址" prop="currentAddress" class="full-row">
              <div class="address-row">
                <div class="address-area" @click="openAreaPicker('current')">
                  <span :class="{ 'is-placeholder': !form.currentAreaText }">{{
                    form.currentAreaText || "请选择省/市/区"
                  }}</span>
                  <i class="el-icon-arrow-down address-area__icon"></i>
                </div>
                <el-input
                  v-model.trim="form.currentAddress"
                  placeholder="请填写详细地址（门牌号等）"
                  class="address-detail"
                />
              </div>
            </el-form-item>

            <el-form-item label="个人邮箱" prop="email">
              <el-input v-model.trim="form.email" placeholder="请输入邮箱" />
            </el-form-item>
            <el-form-item label="紧急联系人" prop="emergencyContact">
              <el-input
                v-model.trim="form.emergencyContact"
                placeholder="请输入紧急联系人姓名"
              />
            </el-form-item>

            <el-form-item label="紧急联系人手机号" prop="emergencyPhone">
              <el-input
                v-model.trim="form.emergencyPhone"
                maxlength="11"
                placeholder="请输入11位手机号"
              />
            </el-form-item>
            <el-form-item label="紧急联系人关系" prop="emergencyRelation">
              <el-select
                v-model="form.emergencyRelation"
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in emergencyRelationOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="招聘渠道" prop="recruitmentChannel">
              <el-select
                v-model="form.recruitmentChannel"
                :loading="optionsLoading"
                placeholder="请选择"
              >
                <el-option
                  v-for="item in recruitmentOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item
              v-if="selectedRecruitmentTitle.includes('派遣')"
              label="派遣公司名称"
              prop="dispatchCompany"
              :rules="requiredRule('请选择派遣公司名称')"
            >
              <el-select
                v-model="form.dispatchCompany"
                :loading="optionsLoading"
                placeholder="请选择派遣公司名称"
                clearable
              >
                <el-option
                  v-for="item in dispatchCompanyOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <template v-if="selectedRecruitmentTitle === '内部推荐'">
              <el-form-item
                label="介绍人姓名"
                prop="introducer"
                :rules="nameRequiredRule('请输入介绍人姓名')"
              >
                <el-input
                  v-model.trim="form.introducer"
                  placeholder="请输入介绍人姓名"
                />
              </el-form-item>
              <el-form-item label="介绍人工号">
                <el-input
                  v-model.trim="form.introducerWorkCode"
                  placeholder="请输入介绍人工号"
                />
              </el-form-item>
            </template>
            <el-form-item
              v-if="selectedRecruitmentTitle === '校园招聘'"
              label="学校名称"
            >
              <el-input
                v-model.trim="form.recruitmentSchool"
                placeholder="请输入招聘学校名称"
              />
            </el-form-item>
            <el-form-item
              v-if="selectedRecruitmentTitle === '网络招聘'"
              label="招聘网站"
            >
              <el-input
                v-model.trim="form.recruitmentWebsite"
                placeholder="请输入招聘网站"
              />
            </el-form-item>
            <el-form-item
              v-if="selectedRecruitmentTitle === '其他'"
              label="其他渠道"
            >
              <el-select v-model="form.restType" placeholder="请选择">
                <el-option label="现场" value="1" />
                <el-option label="广告" value="2" />
              </el-select>
            </el-form-item>

            <el-form-item label="银行卡号" prop="bankCard">
              <el-input
                v-model.trim="form.bankCard"
                maxlength="19"
                placeholder="请输入16-19位银行卡号"
              />
            </el-form-item>

            <el-form-item label="开户行" prop="bankName">
              <el-input
                v-model.trim="form.bankName"
                placeholder="示例：兴业银行股份有限公司惠州金山湖支行"
              />
            </el-form-item>
            <!-- <el-form-item label="填写人" prop="filledBy">
              <el-input v-model.trim="form.filledBy" placeholder="请输入填写人" />
            </el-form-item> -->

            <el-form-item label="填写时间">
              <el-input v-model="form.filledAt" readonly />
            </el-form-item>
          </div>
        </section>

        <section v-show="activeStep === 1" class="step-content">
          <div class="section-title">
            <i class="el-icon-s-custom"></i>
            <span>家庭成员信息</span>
            <em>（至少填写2位）</em>
          </div>

          <div
            v-for="(member, index) in form.familyMembers"
            :key="member.key"
            class="repeat-card"
          >
            <div class="repeat-card-header">
              <strong>家庭成员 {{ index + 1 }}</strong>
              <el-button
                v-if="form.familyMembers.length > MIN_FAMILY_MEMBERS"
                type="danger"
                size="small"
                @click="removeItem('familyMembers', index)"
              >
                删除
              </el-button>
            </div>
            <div class="form-grid">
              <el-form-item
                label="家庭成员姓名"
                :prop="`familyMembers.${index}.name`"
                :rules="familyNameRules(index)"
              >
                <el-input
                  v-model.trim="member.name"
                  placeholder="请输入姓名"
                  @blur="revalidateFamilyField('name', index)"
                />
              </el-form-item>
              <el-form-item
                label="与本人关系"
                :prop="`familyMembers.${index}.relation`"
                :rules="familyRelationRules(index)"
              >
                <el-select
                  v-model="member.relation"
                  :loading="optionsLoading"
                  placeholder="请选择"
                  @change="revalidateFamilyField('relation', index)"
                >
                  <el-option
                    v-for="item in familyRelationOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
              <el-form-item
                label="联系电话"
                :prop="`familyMembers.${index}.phone`"
                :rules="familyPhoneRules(index)"
              >
                <el-input
                  v-model.trim="member.phone"
                  maxlength="11"
                  placeholder="请输入11位手机号"
                  @blur="revalidateFamilyField('phone', index)"
                />
              </el-form-item>
              <el-form-item label="工作单位">
                <el-input v-model.trim="member.company" placeholder="请输入工作单位" />
              </el-form-item>
              <el-form-item label="职务">
                <el-input v-model.trim="member.position" placeholder="请输入职务" />
              </el-form-item>
              <el-form-item label="联系地址" class="full-row">
                <el-input
                  v-model.trim="member.address"
                  placeholder="请输入联系地址"
                />
              </el-form-item>
            </div>
          </div>
          <el-button class="add-button" type="success" @click="addFamilyMember">
            + 新增家庭成员
          </el-button>
        </section>

        <section v-show="activeStep === 2" class="step-content">
          <div class="section-title">
            <i class="el-icon-reading"></i>
            <span>教育信息</span>
          </div>

          <div
            v-for="(education, index) in form.educations"
            :key="education.key"
            class="repeat-card"
          >
            <div class="repeat-card-header">
              <strong>教育经历 {{ index + 1 }}</strong>
              <el-button
                v-if="form.educations.length > 1"
                type="danger"
                size="small"
                @click="removeItem('educations', index)"
              >
                删除
              </el-button>
            </div>
            <div class="form-grid">
              <el-form-item
                label="学校名称"
                :prop="`educations.${index}.school`"
                :rules="requiredRule('请输入学校名称')"
              >
                <el-input v-model.trim="education.school" placeholder="请输入学校名称" />
              </el-form-item>
              <el-form-item
                label="学历"
                :prop="`educations.${index}.degree`"
                :rules="requiredRule('请选择学历', 'change')"
              >
                <el-select
                  v-model="education.degree"
                  :loading="optionsLoading"
                  placeholder="请选择"
                >
                  <el-option
                    v-for="item in degreeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="专业">
                <el-input v-model.trim="education.major" placeholder="请输入专业" />
              </el-form-item>
              <el-form-item label="学制（年）">
                <el-input
                  v-model.trim="education.duration"
                  maxlength="2"
                  placeholder="请输入学制"
                />
              </el-form-item>
              <el-form-item
                label="入学时间"
                :prop="`educations.${index}.startDate`"
                :rules="requiredRule('请选择入学时间', 'change')"
              >
                <mobile-date-picker
                  v-model="education.startDate"
                  placeholder="请选择入学时间"
                />
              </el-form-item>
              <el-form-item
                label="毕业时间"
                :prop="`educations.${index}.endDate`"
                :rules="requiredRule('请选择毕业时间', 'change')"
              >
                <mobile-date-picker
                  v-model="education.endDate"
                  placeholder="请选择毕业时间"
                />
              </el-form-item>
              <el-form-item
                label="学习形式"
                :prop="`educations.${index}.studyMode`"
                :rules="requiredRule('请选择学习形式', 'change')"
              >
                <el-select
                  v-model="education.studyMode"
                  :loading="optionsLoading"
                  placeholder="请选择"
                >
                  <el-option
                    v-for="item in studyModeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="证书编号">
                <el-input
                  v-model.trim="education.certificateNo"
                  placeholder="请输入证书编号"
                />
              </el-form-item>
            </div>
          </div>
          <el-button class="add-button" type="success" @click="addEducation">
            + 新增教育经历
          </el-button>
        </section>

        <section v-show="activeStep === 3" class="step-content">
          <div class="section-title">
            <i class="el-icon-suitcase"></i>
            <span>工作经历</span>
          </div>

          <div
            v-for="(work, index) in form.workExperiences"
            :key="work.key"
            class="repeat-card"
          >
            <div class="repeat-card-header">
              <strong>工作经历 {{ index + 1 }}</strong>
              <el-button
                v-if="form.workExperiences.length > 1"
                type="danger"
                size="small"
                @click="removeItem('workExperiences', index)"
              >
                删除
              </el-button>
            </div>
            <div class="form-grid">
              <el-form-item
                label="工作单位名称"
                :prop="`workExperiences.${index}.company`"
                :rules="requiredRule('请输入工作单位名称')"
              >
                <el-input
                  v-model.trim="work.company"
                  placeholder="请输入工作单位；无工作经历请填“无”"
                  @input="handleWorkCompanyInput(index)"
                />
              </el-form-item>
              <el-form-item label="所属行业">
                <el-input v-model.trim="work.industry" placeholder="请输入所属行业" />
              </el-form-item>
              <el-form-item label="部门">
                <el-input v-model.trim="work.department" placeholder="请输入部门" />
              </el-form-item>
              <el-form-item
                label="职务"
                :prop="`workExperiences.${index}.position`"
                :rules="workRequiredRule(work, '请输入职务')"
              >
                <el-input v-model.trim="work.position" placeholder="请输入职务" />
              </el-form-item>
              <el-form-item
                label="入职时间"
                :prop="`workExperiences.${index}.startDate`"
                :rules="workRequiredRule(work, '请选择入职时间', 'change')"
              >
                <mobile-date-picker
                  v-model="work.startDate"
                  placeholder="请选择入职时间"
                />
              </el-form-item>
              <el-form-item
                label="离职时间"
                :prop="`workExperiences.${index}.endDate`"
                :rules="workEndDateRules(work)"
              >
                <mobile-date-picker
                  v-model="work.endDate"
                  placeholder="请选择离职时间"
                />
              </el-form-item>
              <el-form-item label="工作内容" class="full-row">
                <el-input
                  v-model.trim="work.content"
                  type="textarea"
                  :rows="4"
                  maxlength="500"
                  show-word-limit
                  placeholder="请输入工作内容"
                />
              </el-form-item>
              <el-form-item
                label="证明人"
                :prop="`workExperiences.${index}.referenceName`"
                :rules="noNumberOnlyRule()"
              >
                <el-input v-model.trim="work.referenceName" placeholder="请输入证明人" />
              </el-form-item>
              <el-form-item label="证明人联系电话" prop="">
                <el-input
                  v-model.trim="work.referencePhone"
                  maxlength="11"
                  placeholder="请输入联系电话"
                />
              </el-form-item>
            </div>
          </div>
          <el-button class="add-button" type="success" @click="addWorkExperience">
            + 新增工作经历
          </el-button>
        </section>

        <section v-show="activeStep === 4" class="step-content confirm-content">
          <div class="section-title">
            <i class="el-icon-document-checked"></i>
            <span>确认提交</span>
          </div>
          <el-alert
            title="请确认以下信息准确无误，提交后请联系管理员处理修改。"
            type="warning"
            :closable="false"
            show-icon
          />

          <div class="summary-card">
            <h3>个人基础信息</h3>
            <div class="summary-grid">
              <div><span>姓名</span>{{ form.name }}</div>
              <div>
                <span>性别</span>{{ getOptionLabel(genderOptions, form.gender) }}
              </div>
              <div><span>出生日期</span>{{ form.birthDate }}</div>
              <div><span>手机号</span>{{ form.phone }}</div>
              <div><span>身份证号</span>{{ form.idCard }}</div>
            </div>
          </div>
          <div class="summary-card">
            <h3>经历信息</h3>
            <div class="summary-count">
              <span>家庭成员：{{ form.familyMembers.length }} 位</span>
              <span>教育经历：{{ form.educations.length }} 条</span>
              <span>工作经历：{{ form.workExperiences.length }} 条</span>
            </div>
          </div>

          <el-checkbox v-model="confirmed" class="confirm-checkbox">
            本人确认以上信息真实、完整，并同意用于办理入职手续
          </el-checkbox>
        </section>
      </el-form>

      <footer class="form-actions">
        <el-button
          v-if="activeStep > 0"
          class="previous-button"
          size="medium"
          @click="previousStep"
        >
          ← 上一步
        </el-button>
        <span v-else></span>
        <el-button
          v-if="activeStep < steps.length - 1"
          class="next-button"
          type="success"
          size="medium"
          @click="nextStep"
        >
          下一步 →
        </el-button>
        <el-button
          v-else
          class="next-button"
          type="success"
          size="medium"
          :loading="submitting"
          @click="submitForm"
        >
          {{ isEditMode ? "确认修改" : "确认提交" }}
        </el-button>
      </footer>
    </div>

    <transition name="success-fade">
      <div
        v-if="successVisible"
        class="submit-success-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-success-title"
      >
        <div class="submit-success-card">
          <div class="success-icon" aria-hidden="true">
            <i class="el-icon-check"></i>
          </div>
          <h2 id="submit-success-title">
            {{ isEditMode ? "修改成功！" : "提交成功！" }}
          </h2>
          <p>
            {{
              isEditMode
                ? "员工入职信息已更新"
                : "您的入职信息已同步至HR系统"
            }}
          </p>
          <div class="success-actions">
            <el-button
              class="success-primary-button"
              type="success"
              @click="handleSuccessAction"
            >
              {{ isEditMode ? "返回详情" : "重新填写" }}
            </el-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from "@/apis";
import utils from "@/utils/utils";
import {
  provinceAndCityData,
  regionData,
  CodeToText,
} from "element-china-area-data";
import { areaList } from "@vant/area-data";
import MobileDatePicker from "@/components/MobileDatePicker.vue";

const DRAFT_KEY = "employee-onboarding-draft";
const MIN_FAMILY_MEMBERS = 2;

function createFamilyMember() {
  return {
    key: `family-${Date.now()}-${Math.random()}`,
    name: "",
    relation: "",
    phone: "",
    company: "",
    position: "",
    address: "",
  };
}

// 新建、草稿恢复和编辑回填时，都保证至少存在两位家庭成员
function normalizeFamilyMembers(members = []) {
  const normalizedMembers = (Array.isArray(members) ? members : []).map(
    (member) => {
      const normalizedMember = {
        ...createFamilyMember(),
        ...member,
        // 每一行重新生成唯一 key，避免旧草稿中重复 key 导致 v-for 复用串位
        key: `family-${Date.now()}-${Math.random()}`,
      };
      // 清理旧草稿中已经取消填写的家庭成员身份证号
      delete normalizedMember.idCard;
      return normalizedMember;
    }
  );
  while (normalizedMembers.length < MIN_FAMILY_MEMBERS) {
    normalizedMembers.push(createFamilyMember());
  }
  return normalizedMembers;
}

function createEducation() {
  return {
    key: `education-${Date.now()}-${Math.random()}`,
    school: "",
    degree: "",
    major: "",
    duration: "",
    startDate: "",
    endDate: "",
    studyMode: "",
    certificateNo: "",
  };
}

// 草稿恢复、编辑回填时，拷贝每一行并保证 key 唯一，避免 v-for key 冲突导致数据串位
function normalizeEducations(educations = []) {
  const list = Array.isArray(educations) ? educations : [];
  if (!list.length) return [createEducation()];
  return list.map((item) => ({
    ...createEducation(),
    ...item,
    key: `education-${Date.now()}-${Math.random()}`,
  }));
}

function createWorkExperience() {
  return {
    key: `work-${Date.now()}-${Math.random()}`,
    company: "",
    industry: "",
    department: "",
    position: "",
    startDate: "",
    endDate: "",
    content: "",
    referenceName: "",
    referencePhone: "",
  };
}

// 同 normalizeEducations，保证工作经历每行 key 唯一
function normalizeWorkExperiences(works = []) {
  const list = Array.isArray(works) ? works : [];
  if (!list.length) return [createWorkExperience()];
  return list.map((item) => ({
    ...createWorkExperience(),
    ...item,
    key: `work-${Date.now()}-${Math.random()}`,
  }));
}

// 仅识别明确表示没有工作经历的内容，避免误判包含“无”字的正常单位名称
function isNoWorkExperienceText(value) {
  const normalizedValue = String(value || "")
    .trim()
    .replace(/[\s，,。.!！、；;：:（）()]/g, "");
  return (
    /^(?:无|暂无|没有|从未有?)(?:相关|任何)?(?:工作|就业)?(?:单位|经历|经验)?$/.test(
      normalizedValue
    ) ||
    /^(?:未参加工作|从未参加工作|未工作|未就业|无业|待业|应届生)$/.test(
      normalizedValue
    )
  );
}

function createInitialForm() {
  return {
    name: "",
    gender: "",
    birthDate: "",
    nation: "",
    nativePlace: [],
    householdType: "",
    maritalStatus: "",
    politicalStatus: "",
    idCard: "",
    idCardExpiry: "",
    idCardAuthority: "",
    phone: "",
    // 省市区编码数组（回填匹配用）与展示文本（提交拼接用），提交时与详细地址拼接后写入原字段
    idCardArea: [],
    idCardAreaText: "",
    currentArea: [],
    currentAreaText: "",
    idCardAddress: "",
    currentAddress: "",
    email: "",
    emergencyContact: "",
    emergencyPhone: "",
    emergencyRelation: "",
    recruitmentChannel: "",
    dispatchCompany: "",
    introducer: "",
    introducerWorkCode: "",
    recruitmentSchool: "",
    recruitmentWebsite: "",
    restType: "",
    bankCard: "",
    bankName: "",
    filledBy: "",
    filledAt: "",
    familyMembers: normalizeFamilyMembers(),
    educations: normalizeEducations(),
    workExperiences: normalizeWorkExperiences(),
  };
}

export default {
  name: "EmployeeInformation",
  components: { MobileDatePicker },
  data() {
    const validateIdCard = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请输入身份证号"));
      } else if (!/^\d{17}[\dXx]$/.test(value)) {
        callback(new Error("请输入正确的18位身份证号"));
      } else {
        callback();
      }
    };
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请输入手机号"));
      } else if (!/^1\d{10}$/.test(value)) {
        callback(new Error("请输入正确的11位手机号"));
      } else {
        callback();
      }
    };
    const validateEmail = (rule, value, callback) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        callback(new Error("请输入正确的邮箱地址"));
      } else {
        callback();
      }
    };
    const validateBankCard = (rule, value, callback) => {
      if (value && !/^\d{16,19}$/.test(value)) {
        callback(new Error("请输入16-19位银行卡号"));
      } else {
        callback();
      }
    };

    return {
      provinceAndCityData,
      // 省市区三级联动数据
      regionData,
      // Vant 省市区数据（手机端底部弹出选择用）
      areaList,
      // 省市区选择弹窗状态
      areaPickerVisible: false,
      areaPickerTarget: "idCard",
      // 弹窗回显用的省市区编码
      currentAreaCode: "",
      MIN_FAMILY_MEMBERS,
      editApplyId: String(this.$route.query.id || ""),
      editLoading: false,
      originalBaseInfo: {},
      activeStep: 0,
      // 身份证专用键盘显隐状态
      idCardKeyboardShow: false,
      // 身份证输入光标位置，支持在中间插入/删除
      idCardCursor: 0,
      confirmed: false,
      submitting: false,
      successVisible: false,
      steps: [
        { title: "基础信息", icon: "el-icon-user" },
        { title: "家庭成员", icon: "el-icon-s-custom" },
        { title: "教育信息", icon: "el-icon-reading" },
        { title: "工作经历", icon: "el-icon-suitcase" },
        { title: "确认提交", icon: "el-icon-document-checked" },
      ],
      optionsLoading: false,
      genderOptions: [],
      householdTypeOptions: [],
      nationOptions: [],
      politicalStatusOptions: [],
      maritalStatusOptions: [],
      emergencyRelationOptions: [],
      familyRelationOptions: [],
      degreeOptions: [],
      studyModeOptions: [],
      recruitmentOptions: [],
      dispatchCompanyOptions: [],
      form: createInitialForm(),
      idCardRules: [
        { required: true, validator: validateIdCard, trigger: "blur" },
      ],
      phoneRules: [
        { required: true, validator: validatePhone, trigger: "blur" },
      ],
      rules: {
        name: [
          { required: true, message: "请输入姓名", trigger: "blur" },
          this.noNumberRule("姓名不能包含数字"),
        ],
        gender: [{ required: true, message: "请选择性别", trigger: "change" }],
        birthDate: [{ required: true, message: "请选择出生日期", trigger: "change" }],
        nation: [{ required: true, message: "请选择民族", trigger: "change" }],
        nativePlace: [{ required: true, message: "请选择籍贯", trigger: "change" }],
        politicalStatus: [
          { required: true, message: "请选择政治面貌", trigger: "change" },
        ],
        householdType: [
          { required: true, message: "请选择户籍性质", trigger: "change" },
        ],
        maritalStatus: [
          { required: true, message: "请选择婚姻状况", trigger: "change" },
        ],
        idCard: [{ required: true, validator: validateIdCard, trigger: "blur" }],
        idCardExpiry: [
          { required: true, message: "请选择身份证有效期", trigger: "change" },
          {
            validator: (rule, value, callback) => {
              if (!value) return callback();
              // 选择"长期"不校验日期
              if (value === "9999-01-01") return callback();
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const selected = new Date(value);
              if (selected < today) {
                callback(new Error("有效期不能早于当前日期"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
        idCardAuthority: [
          { required: true, message: "请输入身份证签发机构", trigger: "blur" },
        ],
        phone: [{ required: true, validator: validatePhone, trigger: "blur" }],
        idCardAddress: [
          {
            validator: (rule, value, callback) => {
              if (!this.form.idCardAreaText) {
                callback(new Error("请选择身份证地址省市区"));
                return;
              }
              if (!value) {
                callback(new Error("请输入身份证详细地址"));
                return;
              }
              callback();
            },
            trigger: "change",
          },
        ],
        currentAddress: [
          {
            validator: (rule, value, callback) => {
              if (!this.form.currentAreaText) {
                callback(new Error("请选择现住址省市区"));
                return;
              }
              if (!value) {
                callback(new Error("请输入现住址详细地址"));
                return;
              }
              callback();
            },
            trigger: "change",
          },
        ],
        email: [{ validator: validateEmail, trigger: "blur" }],
        emergencyContact: [
          { required: true, message: "请输入紧急联系人", trigger: "blur" },
          this.noNumberRule("紧急联系人不能包含数字"),
        ],
        emergencyPhone: [
          { required: true, validator: validatePhone, trigger: "blur" },
        ],
        emergencyRelation: [
          { required: true, message: "请选择紧急联系人关系", trigger: "change" },
        ],
        recruitmentChannel: [
          { required: true, message: "请选择招聘渠道", trigger: "change" },
        ],
        bankCard: [{ validator: validateBankCard, trigger: "blur" }],
        // filledBy: [{ required: true, message: "请输入填写人", trigger: "blur" }],
      },
    };
  },
  computed: {
    isEditMode() {
      return Boolean(this.editApplyId);
    },
    // 是否移动端：移动端用 Vant 数字键盘，PC 端用原生可编辑输入框
    isMobileDevice() {
      try {
        return Boolean(utils.parseUA().mobile);
      } catch (e) {
        return false;
      }
    },
    selectedRecruitmentTitle() {
      return this.getOptionLabel(
        this.recruitmentOptions,
        this.form.recruitmentChannel
      );
    },
  },
  watch: {
    form: {
      deep: true,
      handler(value) {
        if (this.isEditMode) return;
        localStorage.setItem(DRAFT_KEY, JSON.stringify(value));
      },
    },
  },
  created() {
    if (this.isEditMode) {
      this.initializeEditForm();
      return;
    }
    this.restoreDraft();
    if (!this.form.filledAt) {
      this.form.filledAt = this.formatDateTime(new Date());
    }
    this.loadOptions();
  },
  methods: {
    getOptionLabel(options, value) {
      const option = options.find((item) => item.value === String(value || ""));
      return option ? option.label : "";
    },
    toFormValue(value) {
      return value === null || value === undefined ? "" : String(value).trim();
    },
    normalizeDateValue(value) {
      const dateText = this.toFormValue(value);
      return !dateText || dateText.startsWith("0000-00-00")
        ? ""
        : dateText.slice(0, 10);
    },
    // 兼容详情接口中带不可见字符的 Introducer、Industry 等字段名
    getCompatibleValue(data, prefix) {
      const target = data || {};
      const key = Object.keys(target).find((item) => item.startsWith(prefix));
      return key ? this.toFormValue(target[key]) : "";
    },
    getNativePlaceCodes(value) {
      if (Array.isArray(value)) return value;
      const nativePlace = this.toFormValue(value);
      if (!nativePlace) return [];

      // 提交值已剔除“省/市”，通过标准省市名称组合反向匹配级联编码
      const normalizedNativePlace = nativePlace.replace(/[省市]/g, "");
      for (const province of provinceAndCityData) {
        const cities = province.children || [];
        for (const city of cities) {
          const regionText = `${province.label}${city.label}`.replace(
            /[省市]/g,
            ""
          );
          if (regionText === normalizedNativePlace) {
            return [province.value, city.value];
          }
        }
      }
      return [];
    },
    mapEditDetailToForm(detail) {
      const base = detail.preEmpinfo || {};
      const emergency = detail.PreBgeminfo || {};
      const familyRows = Array.isArray(detail.PreFamilyinfo)
        ? detail.PreFamilyinfo
        : [];
      const educationRows = Array.isArray(detail.PreEduinfo)
        ? detail.PreEduinfo
        : [];
      const workRows = Array.isArray(detail.PreWorkexpinfo)
        ? detail.PreWorkexpinfo
        : [];

      return {
        ...createInitialForm(),
        name: this.toFormValue(base.Title),
        gender: this.toFormValue(base.Gender),
        birthDate: this.normalizeDateValue(base.BirthDate),
        nation: this.toFormValue(base.Nation),
        nativePlace: this.getNativePlaceCodes(base.NativePlace),
        householdType: this.toFormValue(base.householdRegister),
        maritalStatus: this.toFormValue(base.MaritalStatus),
        politicalStatus: this.toFormValue(base.Party),
        idCard: this.toFormValue(base.IDNo),
        idCardExpiry: this.normalizeDateValue(base.IDENDDATE),
        idCardAuthority: this.toFormValue(base.IDAddress),
        phone: this.toFormValue(base.MobileNo),
        ...this.splitAddressFields(
          this.toFormValue(base.PermanentAddress),
          this.toFormValue(base.LiveAddress)
        ),
        email: this.toFormValue(base.Email_P),
        emergencyContact: this.toFormValue(emergency.EmContact),
        emergencyPhone: this.toFormValue(emergency.EmContactTel),
        emergencyRelation: this.toFormValue(emergency.EmContactRel),
        recruitmentChannel: this.toFormValue(base.Channel),
        dispatchCompany: this.toFormValue(base.PqCompany),
        introducer: this.getCompatibleValue(base, "Introducer"),
        introducerWorkCode: this.toFormValue(base.InWorkCode),
        recruitmentSchool: this.toFormValue(base.School),
        recruitmentWebsite: this.toFormValue(base.Website),
        restType:
          Number(base.RestType) === 0 ? "" : this.toFormValue(base.RestType),
        bankCard: this.toFormValue(base.BankCode),
        bankName: this.toFormValue(base.Bank),
        filledBy: this.toFormValue(base.Submitor),
        filledAt:
          (this.toFormValue(base.created).startsWith("0000-00-00")
            ? ""
            : this.toFormValue(base.created)) ||
          this.formatDateTime(new Date()),
        familyMembers: normalizeFamilyMembers(
          familyRows.map((row) => ({
            ...createFamilyMember(),
            name: this.toFormValue(row.Name),
            relation: this.toFormValue(row.Relation),
            phone: this.toFormValue(row.TelNo),
            company: this.toFormValue(row.WorkOrg),
            position: this.toFormValue(row.WorkPost),
            address: this.toFormValue(row.HJAddress),
          }))
        ),
        educations: normalizeEducations(
          educationRows.map((row) => ({
            ...createEducation(),
            school: this.toFormValue(row.School),
            degree: this.toFormValue(row.Education),
            major: this.toFormValue(row.Major),
            duration: this.toFormValue(row.Years),
            startDate: this.normalizeDateValue(row.BeginDate),
            endDate: this.normalizeDateValue(row.EndDate),
            studyMode:
              Number(row.StudyType) === 0
                ? ""
                : this.toFormValue(row.StudyType),
            certificateNo: this.toFormValue(row.No),
          }))
        ),
        workExperiences: normalizeWorkExperiences(
          workRows.map((row) => ({
            ...createWorkExperience(),
            company: this.toFormValue(row.Organization),
            industry: this.getCompatibleValue(row, "Industry"),
            department: this.toFormValue(row.OrgName),
            position: this.toFormValue(row.Position),
            startDate: this.normalizeDateValue(row.BeginDate),
            endDate: this.normalizeDateValue(row.EndDate),
            content: this.toFormValue(row.Duty),
            referenceName: this.toFormValue(row.referenceBy),
            referencePhone: this.toFormValue(row.referenceTel),
          }))
        ),
      };
    },
    async initializeEditForm() {
      this.editLoading = true;
      try {
        const [detailRes] = await Promise.all([
          api.getPreEmployeeDetail({
            id: this.editApplyId,
          }),
          this.loadOptions(),
        ]);
        const detail = detailRes.data || {};
        this.originalBaseInfo = detail.preEmpinfo || {};
        this.form = this.mapEditDetailToForm(detail);
        this.migrateDraftOptionValues();
        this.$nextTick(() => {
          if (this.$refs.employeeForm) {
            this.$refs.employeeForm.clearValidate();
          }
        });
      } catch (error) {
        this.$message.error(
          error.message || "员工信息加载失败，请返回列表后重试"
        );
      } finally {
        this.editLoading = false;
      }
    },
    normalizeOptionList(data) {
      // 兼容派遣公司等直接返回字符串数组的情况
      if (Array.isArray(data) && data.every((item) => typeof item === "string")) {
        return data
          .filter(Boolean)
          .map((item) => ({
            value: item,
            label: item,
            sort: 0,
          }));
      }
      return Object.values(data || {})
        .map((item) => {
          // 兼容字典接口可能返回的大小写字段
          const value = item.ID || item.orgid;
          const label = item.Title || item.title;
          return {
            value: value ? String(value) : "",
            label: String(label || "")
              .replace(/\r?\n/g, "")
              .trim(),
            sort: Number(item.ROW_NUMBER) || 0,
          };
        })
        .filter((item) => item.value && item.label)
        .sort((first, second) => first.sort - second.sort);
    },
    normalizeOptionValue(options, value) {
      if (!value || !options.length) return value;
      const currentValue = String(value);
      const valueOption = options.find((item) => item.value === currentValue);
      if (valueOption) return valueOption.value;
      const labelOption = options.find((item) => item.label === currentValue);
      return labelOption ? labelOption.value : "";
    },
    // 将旧草稿中的中文选项转换为接口 ID，避免提交历史文本值
    migrateDraftOptionValues() {
      const fieldOptions = [
        ["gender", this.genderOptions],
        // 此处第一列应为 form 字段名 householdType，而非后端字段名 householdRegister，否则草稿中文值无法迁移为 ID
        ["householdType", this.householdTypeOptions],
        ["nation", this.nationOptions],
        ["politicalStatus", this.politicalStatusOptions],
        ["maritalStatus", this.maritalStatusOptions],
        ["emergencyRelation", this.emergencyRelationOptions],
        ["recruitmentChannel", this.recruitmentOptions],
      ];
      fieldOptions.forEach(([field, options]) => {
        this.form[field] = this.normalizeOptionValue(
          options,
          this.form[field]
        );
      });
      this.form.familyMembers.forEach((member) => {
        member.relation = this.normalizeOptionValue(
          this.familyRelationOptions,
          member.relation
        );
      });
      this.form.educations.forEach((education) => {
        education.degree = this.normalizeOptionValue(
          this.degreeOptions,
          education.degree
        );
        // 学习形式由静态选项改为接口选项，需将历史中文值（全日制等）迁移为接口 ID
        education.studyMode = this.normalizeOptionValue(
          this.studyModeOptions,
          education.studyMode
        );
      });
    },
    async loadOptions() {
      const optionConfigs = [
        { type: "Gender", field: "genderOptions" },
        { type: "householdRegister", field: "householdTypeOptions" },
        { type: "Nation", field: "nationOptions" },
        { type: "Party", field: "politicalStatusOptions" },
        { type: "MaritalStatus", field: "maritalStatusOptions" },
        { type: "EmContactRel", field: "emergencyRelationOptions" },
        { type: "Relation", field: "familyRelationOptions" },
        { type: "Education", field: "degreeOptions" },
        { type: "StudyType", field: "studyModeOptions" },
        { type: "JoinType", field: "recruitmentOptions" },
        { type: "pqcompany", field: "dispatchCompanyOptions" },
      ];
      this.optionsLoading = true;
      try {
        const results = await Promise.all(
          optionConfigs.map(async (config) => {
            try {
              const res = await api.getPreEmployeeOptions({
                type: config.type,
              });
              return {
                ...config,
                options: this.normalizeOptionList(res.data),
                failed: false,
              };
            } catch (error) {
              return { ...config, options: [], failed: true };
            }
          })
        );
        results.forEach((result) => {
          this[result.field] = result.options;
        });
        this.migrateDraftOptionValues();
        if (results.some((result) => result.failed)) {
          this.$message.warning("部分下拉选项加载失败，请刷新页面重试");
        }
      } finally {
        this.optionsLoading = false;
      }
    },
    requiredRule(message, trigger = "blur") {
      return [{ required: true, message, trigger }];
    },
    // "人名不含数字"通用校验规则对象，可拼接到任意规则数组
    noNumberRule(message, trigger = "blur") {
      return {
        validator: (rule, value, callback) => {
          if (!value) return callback();
          callback(/\d/.test(value) ? new Error(message) : undefined);
        },
        trigger,
      };
    },
    // 必填人名校验：必填 + 不含数字
    nameRequiredRule(message) {
      return [
        { required: true, message, trigger: "blur" },
        this.noNumberRule("姓名不能包含数字"),
      ];
    },
    // 非必填人名校验：仅限制不含数字
    noNumberOnlyRule() {
      return [this.noNumberRule("姓名不能包含数字")];
    },
    // 判断指定家庭成员的某字段值是否与其他行重复（姓名/手机号/关系各自独立不可重复）
    isFamilyFieldDuplicate(field, index) {
      const current = this.form.familyMembers[index];
      if (!current) return false;
      const value = current[field];
      if (!value) return false;
      return this.form.familyMembers.some(
        (m, i) => i !== index && m[field] && m[field] === value
      );
    },
    // 家庭成员姓名校验：必填 + 不含数字 + 姓名不可重复
    familyNameRules(index) {
      return [
        { required: true, message: "请输入家庭成员姓名", trigger: "blur" },
        this.noNumberRule("家庭成员姓名不能包含数字"),
        {
          validator: (rule, value, callback) => {
            if (!value) return callback();
            callback(
              this.isFamilyFieldDuplicate("name", index)
                ? new Error("家庭成员姓名不能重复")
                : undefined
            );
          },
          trigger: "blur",
        },
      ];
    },
    // 家庭成员手机号校验：必填 + 格式 + 手机号不可重复
    familyPhoneRules(index) {
      return [
        {
          validator: (rule, value, callback) => {
            if (!value) {
              callback(new Error("请输入手机号"));
              return;
            }
            if (!/^1\d{10}$/.test(value)) {
              callback(new Error("请输入正确的11位手机号"));
              return;
            }
            callback(
              this.isFamilyFieldDuplicate("phone", index)
                ? new Error("家庭成员手机号不能重复")
                : undefined
            );
          },
          trigger: "blur",
        },
      ];
    },
    // 家庭成员关系校验：必填 + 关系不可重复
    familyRelationRules(index) {
      return [
        { required: true, message: "请选择与本人关系", trigger: "change" },
        {
          validator: (rule, value, callback) => {
            if (!value) return callback();
            callback(
              this.isFamilyFieldDuplicate("relation", index)
                ? new Error("家庭成员关系不能重复")
                : undefined
            );
          },
          trigger: "change",
        },
      ];
    },
    // 修改家庭成员任一字段后，重新校验其他行的同名字段，及时更新重复提示
    revalidateFamilyField(field, currentIndex) {
      this.$nextTick(() => {
        if (!this.$refs.employeeForm) return;
        this.form.familyMembers.forEach((_, index) => {
          if (index === currentIndex) return;
          this.$refs.employeeForm.validateField(
            `familyMembers.${index}.${field}`
          );
        });
      });
    },
    // 工作单位明确填写“无工作经历”等内容后，其余工作经历字段不再必填
    workRequiredRule(work, message, trigger = "blur") {
      return this.isWorkExperienceOptional(work)
        ? []
        : this.requiredRule(message, trigger);
    },
    // 工作经历离职时间校验：可选工作经历不校验；必填 + 不能晚于今天
    workEndDateRules(work) {
      if (this.isWorkExperienceOptional(work)) return [];
      return [
        { required: true, message: "请选择离职时间", trigger: "change" },
        {
          validator: (rule, value, callback) => {
            if (!value) return callback();
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            const selected = new Date(value);
            if (selected > today) {
              callback(new Error("离职时间不能晚于今天"));
            } else {
              callback();
            }
          },
          trigger: "change",
        },
      ];
    },
    isWorkExperienceOptional(work) {
      return isNoWorkExperienceText(work && work.company);
    },
    handleWorkCompanyInput(index) {
      this.$nextTick(() => {
        const work = this.form.workExperiences[index];
        if (!this.isWorkExperienceOptional(work) || !this.$refs.employeeForm) {
          return;
        }
        this.$refs.employeeForm.clearValidate([
          `workExperiences.${index}.position`,
          `workExperiences.${index}.startDate`,
          `workExperiences.${index}.endDate`,
        ]);
      });
    },
    // 身份证号规范化：统一大写、仅保留数字与末位 X、截断 18 位
    normalizeIdCard(value) {
      let idCard = String(value || "")
        .toUpperCase()
        .replace(/[^\dX]/g, "");
      if (idCard.length > 18) idCard = idCard.slice(0, 18);
      // X 仅允许出现在末位，否则清除所有 X
      if (idCard.indexOf("X") !== -1 && idCard.indexOf("X") !== idCard.length - 1) {
        idCard = idCard.replace(/X/g, "");
      }
      return idCard;
    },
    // 号码完整且出生日期合法时自动回填出生日期
    handleIdCardInput(value) {
      const idCard = String(value || "");
      if (!/^\d{17}[\dX]$/.test(idCard)) return;

      const birthText = idCard.slice(6, 14);
      const year = Number(birthText.slice(0, 4));
      const month = Number(birthText.slice(4, 6));
      const day = Number(birthText.slice(6, 8));
      const birthDate = new Date(year, month - 1, day);
      const isValidDate =
        birthDate.getFullYear() === year &&
        birthDate.getMonth() === month - 1 &&
        birthDate.getDate() === day;
      if (!isValidDate) return;

      this.form.birthDate = `${birthText.slice(0, 4)}-${birthText.slice(
        4,
        6
      )}-${birthText.slice(6, 8)}`;
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.clearValidate("birthDate");
        }
      });
    },
    // PC 端直接输入时规范化（统一大写 X、过滤非法字符、X 仅末位），并回填出生日期
    onPcIdCardInput(value) {
      const normalized = this.normalizeIdCard(value);
      const raw = String(value || "");
      // 规范化后与原值不同则回写（幂等，不会死循环）
      if (normalized !== raw) {
        this.form.idCard = normalized;
      }
      this.handleIdCardInput(normalized);
    },
    // 点击身份证输入框：移动端定位光标并弹出专用数字键盘；PC 端直接用系统键盘，不弹键盘
    onIdCardFieldClick() {
      if (!this.isMobileDevice) return;
      const len = this.form.idCard ? this.form.idCard.length : 0;
      // 默认光标在末尾；readonly 框的 selectionStart 各浏览器不一致，
      // 仅当读取值落在 (0, len] 合理区间时才采用，避免读到 0 导致删除失效
      this.idCardCursor = len;
      const input =
        this.$refs.idCardInput &&
        this.$refs.idCardInput.$el.querySelector("input");
      if (input) {
        try {
          const start = input.selectionStart;
          if (start != null && start > 0 && start <= len) {
            this.idCardCursor = start;
          }
        } catch (e) {}
      }
      this.idCardKeyboardShow = true;
    },
    // 键盘输入：在光标处插入字符，统一大写并限制 X 仅末位
    onIdCardKeyInput(key) {
      const current = String(this.form.idCard || "");
      if (current.length >= 18) return;
      // 光标越界时回退到末尾追加，保证一定能输入
      let cursor = this.idCardCursor;
      if (cursor < 0 || cursor > current.length) cursor = current.length;
      const ch = key === "x" ? "X" : key;
      const inserted = current.slice(0, cursor) + ch + current.slice(cursor);
      const normalized = this.normalizeIdCard(inserted);
      this.form.idCard = normalized;
      this.idCardCursor =
        normalized.length > current.length
          ? Math.min(cursor + 1, normalized.length)
          : cursor;
      this.handleIdCardInput(normalized);
    },
    // 键盘删除：删除光标前一位；若光标异常导致未删除，则回退删除最后一位
    onIdCardKeyDelete() {
      const current = String(this.form.idCard || "");
      if (!current) return;
      const pos = Math.max(0, this.idCardCursor - 1);
      let next = current.slice(0, pos) + current.slice(this.idCardCursor);
      // 光标在开头或越界时，slice 不会减少长度，此时回退删除末位
      if (next.length === current.length) {
        next = current.slice(0, -1);
        this.idCardCursor = next.length;
      } else {
        this.idCardCursor = pos;
      }
      this.form.idCard = next;
    },
    // 键盘关闭：触发身份证号校验
    onIdCardKeyboardBlur() {
      this.idCardKeyboardShow = false;
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.validateField("idCard");
        }
      });
    },
    restoreDraft() {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (!draft) return;
      try {
        const parsed = JSON.parse(draft);
        // 基础信息已移除部门字段，恢复旧草稿时同步清理历史数据
        delete parsed.department;
        this.form = {
          ...createInitialForm(),
          ...parsed,
          familyMembers: normalizeFamilyMembers(parsed.familyMembers),
          educations: normalizeEducations(parsed.educations),
          workExperiences: normalizeWorkExperiences(parsed.workExperiences),
        };
        // 兼容旧草稿：地址为完整文本（无省市区文本）时拆分为省市区 + 详细地址
        if (!this.form.idCardAreaText) {
          Object.assign(
            this.form,
            this.splitAddressFields(
              this.form.idCardAddress,
              this.form.currentAddress
            )
          );
        }
      } catch (error) {
        localStorage.removeItem(DRAFT_KEY);
      }
    },
    formatDateTime(date) {
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    },
    getStepFields(step) {
      if (step === 0) {
        return Object.keys(this.rules);
      }
      if (step === 1) {
        return this.form.familyMembers.reduce((fields, member, index) => {
          fields.push(
            `familyMembers.${index}.name`,
            `familyMembers.${index}.relation`,
            `familyMembers.${index}.phone`
          );
          return fields;
        }, []);
      }
      if (step === 2) {
        return this.form.educations.reduce((fields, education, index) => {
          fields.push(
            `educations.${index}.school`,
            `educations.${index}.degree`,
            `educations.${index}.startDate`,
            `educations.${index}.endDate`
          );
          return fields;
        }, []);
      }
      if (step === 3) {
        return this.form.workExperiences.reduce((fields, work, index) => {
          fields.push(`workExperiences.${index}.company`);
          if (!this.isWorkExperienceOptional(work)) {
            fields.push(
              `workExperiences.${index}.position`,
              `workExperiences.${index}.startDate`,
              `workExperiences.${index}.endDate`
            );
          }
          return fields;
        }, []);
      }
      return [];
    },
    validateCurrentStep() {
      const fields = this.getStepFields(this.activeStep);
      if (!fields.length) return Promise.resolve(true);
      return new Promise((resolve) => {
        let pending = fields.length;
        let valid = true;
        this.$refs.employeeForm.validateField(fields, (message) => {
          if (message) valid = false;
          pending -= 1;
          if (pending === 0) resolve(valid);
        });
      });
    },
    async nextStep() {
      // const valid = await this.validateCurrentStep();
      // if (!valid) {
      //   this.$message.warning("请先完善当前页面的必填信息");
      //   return;
      // }
      this.activeStep += 1;
      this.scrollToTop();
    },
    previousStep() {
      this.activeStep -= 1;
      this.scrollToTop();
    },
    // 点击顶部步骤导航时，直接切换到对应的表单步骤
    goToStep(stepIndex) {
      if (stepIndex === this.activeStep) return;
      this.activeStep = stepIndex;
      this.scrollToTop();
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    addFamilyMember() {
      this.form.familyMembers = [
        ...this.form.familyMembers,
        createFamilyMember(),
      ];
    },
    addEducation() {
      this.form.educations = [...this.form.educations, createEducation()];
    },
    addWorkExperience() {
      this.form.workExperiences = [
        ...this.form.workExperiences,
        createWorkExperience(),
      ];
    },
    // 清除被删除行对应的表单校验，避免 el-form 对已不存在字段残留错误提示
    clearRepeatedFieldValidate(field, index) {
      const formRef = this.$refs.employeeForm;
      if (!formRef || !formRef.fields) return;
      const prefix = `${field}.${index}.`;
      formRef.fields
        .filter((item) => item && item.prop && item.prop.startsWith(prefix))
        .forEach((item) => {
          formRef.clearValidate(item.prop);
        });
    },
    removeItem(field, index) {
      if (
        field === "familyMembers" &&
        this.form.familyMembers.length <= MIN_FAMILY_MEMBERS
      ) {
        this.$message.warning("家庭成员至少需要填写2位");
        return;
      }
      // 先清除被删除行在校验表里的记录
      this.clearRepeatedFieldValidate(field, index);
      const newList = this.form[field].slice();
      newList.splice(index, 1);
      this.form[field] = newList;
      // 删除后清理整个表单的残留校验，避免行号错位导致的校验提示
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.clearValidate();
        }
      });
    },

    getNativePlace() {
      return (this.form.nativePlace || [])
        .map((code) => CodeToText[code])
        .filter(Boolean)
        // 后端籍贯不接收省、市后缀，如“广东省梅州市”转换为“广东梅州”
        .map((regionName) => regionName.replace(/[省市]$/, ""))
        .join("");
    },
    // 省市区编码数组转换为地址文本（保留省/市/区后缀，用于拼接完整地址）
    getAreaText(areaCodes) {
      return (areaCodes || [])
        .map((code) => CodeToText[code])
        .filter(Boolean)
        .join("");
    },
    // 将完整地址文本拆分为省市区编码、省市区文本与详细地址；匹配不到则全部作为详细地址
    splitAddressToArea(fullText) {
      const text = String(fullText || "").trim();
      const result = { areaCodes: [], areaText: "", detail: text };
      if (!text) return result;
      let remaining = text;
      // 省
      for (const province of regionData) {
        if (remaining.startsWith(province.label)) {
          result.areaCodes.push(province.value);
          remaining = remaining.slice(province.label.length);
          // 市
          for (const city of province.children || []) {
            if (remaining.startsWith(city.label)) {
              result.areaCodes.push(city.value);
              remaining = remaining.slice(city.label.length);
              // 区
              for (const district of city.children || []) {
                if (remaining.startsWith(district.label)) {
                  result.areaCodes.push(district.value);
                  remaining = remaining.slice(district.label.length);
                  break;
                }
              }
              break;
            }
          }
          break;
        }
      }
      result.detail = remaining;
      result.areaText = this.getAreaText(result.areaCodes);
      return result;
    },
    // 同时拆分身份证地址与现住址，供加载回填时展开赋值
    splitAddressFields(idCardFull, currentFull) {
      const idCard = this.splitAddressToArea(idCardFull);
      const current = this.splitAddressToArea(currentFull);
      return {
        idCardArea: idCard.areaCodes,
        idCardAreaText: idCard.areaText,
        idCardAddress: idCard.detail,
        currentArea: current.areaCodes,
        currentAreaText: current.areaText,
        currentAddress: current.detail,
      };
    },
    // 打开省市区选择弹窗，回显当前已选编码
    openAreaPicker(type) {
      this.areaPickerTarget = type;
      const codes =
        type === "idCard" ? this.form.idCardArea : this.form.currentArea;
      this.currentAreaCode = codes && codes.length ? codes[codes.length - 1] : "";
      this.areaPickerVisible = true;
    },
    // 省市区选择确认：写回编码、文本，并触发对应地址字段校验
    onAreaConfirm(value) {
      if (!value || !value.length) return;
      const codes = value.map((item) => item.code);
      const text = value.map((item) => item.name).join("");
      if (this.areaPickerTarget === "idCard") {
        this.form.idCardArea = codes;
        this.form.idCardAreaText = text;
      } else {
        this.form.currentArea = codes;
        this.form.currentAreaText = text;
      }
      this.areaPickerVisible = false;
      this.$nextTick(() => {
        if (this.$refs.employeeForm) {
          this.$refs.employeeForm.validateField(
            this.areaPickerTarget === "idCard" ? "idCardAddress" : "currentAddress"
          );
        }
      });
    },
    buildSubmitPayload() {
      const channelTitle = this.selectedRecruitmentTitle;
      const isInternalReferral = channelTitle === "内部推荐";
      const isDispatch = channelTitle.includes("派遣");
      const isCampus = channelTitle === "校园招聘";
      const isWebsite = channelTitle === "网络招聘";
      const isOther = channelTitle === "其他";

      const payload = {
        Title: this.form.name,
        Gender: this.form.gender,
        BirthDate: this.form.birthDate,
        Nation: this.form.nation,
        NativePlace: this.getNativePlace(),
        householdRegister: this.form.householdType,
        MaritalStatus: this.form.maritalStatus,
        Party: this.form.politicalStatus,
        IDNo: this.form.idCard,
        IDENDDATE: this.form.idCardExpiry,
        IDAddress: this.form.idCardAuthority,
        MobileNo: this.form.phone,
        PermanentAddress: this.form.idCardAreaText + this.form.idCardAddress,
        LiveAddress: this.form.currentAreaText + this.form.currentAddress,
        Email_P: this.form.email,
        Channel: this.form.recruitmentChannel,
        PqCompany: isDispatch ? this.form.dispatchCompany : "",
        Introducer: isInternalReferral ? this.form.introducer : "",
        InWorkCode: isInternalReferral ? this.form.introducerWorkCode : "",
        School: isCampus ? this.form.recruitmentSchool : "",
        Website: isWebsite ? this.form.recruitmentWebsite : "",
        RestType: isOther ? this.form.restType : "",
        BankCode: this.form.bankCard,
        Bank: this.form.bankName,
        EmContact: this.form.emergencyContact,
        EmContactRel: this.form.emergencyRelation,
        EmContactTel: this.form.emergencyPhone,
        Submitor: this.form.filledBy,
        familyInfo: this.form.familyMembers.map((member) => ({
          Name: member.name,
          Relation: member.relation,
          // 家庭成员已取消身份证号填写，保留空字段兼容接口结构
          IDNo: "",
          TelNo: member.phone,
          WorkOrg: member.company,
          WorkPost: member.position,
          HJAddress: member.address,
        })),
        eduInfo: this.form.educations.map((education) => ({
          School: education.school,
          Education: education.degree,
          Major: education.major,
          Years: education.duration,
          BeginDate: education.startDate,
          EndDate: education.endDate,
          StudyType: education.studyMode,
          No: education.certificateNo,
        })),
        workInfo: this.form.workExperiences.map((work) => ({
          Organization: work.company,
          Industry: work.industry,
          OrgName: work.department,
          Position: work.position,
          BeginDate: work.startDate,
          EndDate: work.endDate,
          Duty: work.content,
          referenceBy: work.referenceName,
          referenceTel: work.referencePhone,
        })),
      };
      if (!this.isEditMode) return payload;

      return {
        id: this.editApplyId,
        ApplyID: this.getCompatibleValue(this.originalBaseInfo, "ApplyID"),
        ...payload,
        // 部门字段已从表单移除，编辑时保留详情接口返回的原始值
        OrgID: this.getCompatibleValue(this.originalBaseInfo, "OrgID"),
        OrgName: this.getCompatibleValue(this.originalBaseInfo, "OrgName"),
      };
    },
    validateBeforeSubmit() {
      return new Promise((resolve) => {
        this.$refs.employeeForm.validate((valid, fields) => {
          resolve({ valid, fields: fields || {} });
        });
      });
    },
    getStepByInvalidFields(fields) {
      const firstField = Object.keys(fields)[0] || "";
      if (firstField.startsWith("familyMembers.")) return 1;
      if (firstField.startsWith("educations.")) return 2;
      if (firstField.startsWith("workExperiences.")) return 3;
      return 0;
    },
    async submitForm() {
      if (this.submitting) return;

      if (this.form.familyMembers.length < MIN_FAMILY_MEMBERS) {
        this.activeStep = 1;
        this.$message.warning("家庭成员至少需要填写2位");
        this.$nextTick(this.scrollToTop);
        return;
      }

      const validation = await this.validateBeforeSubmit();
      if (!validation.valid) {
        this.activeStep = this.getStepByInvalidFields(validation.fields);
        this.$message.warning("请先完善必填信息");
        this.$nextTick(this.scrollToTop);
        return;
      }

      if (!this.confirmed) {
        this.$message.warning("请勾选信息真实性确认");
        return;
      }
      this.submitting = true;
      try {
        const payload = this.buildSubmitPayload();
        if (this.isEditMode) {
          await api.editPreEmployee(payload);
        } else {
          await api.addPreEmployee(payload);
          localStorage.removeItem(DRAFT_KEY);
        }
        this.$emit("submit", payload);
        // 接口成功后使用独立结果弹层，避免普通消息提示一闪而过
        this.successVisible = true;
      } catch (error) {
        const fallbackMessage = this.isEditMode
          ? "员工信息修改失败，请稍后重试"
          : "员工信息提交失败，请稍后重试";
        this.$message.error(error.message || fallbackMessage);
      } finally {
        this.submitting = false;
      }
    },
    closeSuccessDialog() {
      this.successVisible = false;
    },
    goBackToDetail() {
      this.$router.push({
        path: "/employee/detail",
        query: {
          id: this.editApplyId,
        },
      });
    },
    goToListMine() {
      this.$router.push("/employee/listMine");
    },
    handleSuccessAction() {
      if (this.isEditMode) {
        this.goBackToDetail();
        return;
      }
      this.resetForm();
    },
    // 清空已提交内容和草稿，恢复为首次填写状态
    resetForm() {
      // this.successVisible = false;
      // this.activeStep = 0;
      // this.confirmed = false;
      // this.form = createInitialForm();
      // this.form.filledAt = this.formatDateTime(new Date());
      // this.$nextTick(() => {
      //   this.$refs.employeeForm.clearValidate();
      //   localStorage.removeItem(DRAFT_KEY);
      //   this.scrollToTop();
      // });
    },
  },
};
</script>

<style lang="scss" scoped>
.employee-page {
  min-height: 100vh;
  padding: 0 20PX 40PX;
  color: #303133;
  background: #1fa463;
}

.employee-shell {
  width: 100%;
  max-width: 1120PX;
  min-height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 0 20PX rgba(0, 0, 0, 0.08);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 106PX;
  padding: 20PX 40PX;
  color: #fff;
  background: linear-gradient(135deg, #1c9e5e, #20aa68);

  h1 {
    margin: 0 0 8PX;
    font-size: 25PX;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14PX;
    color: rgba(255, 255, 255, 0.85);
  }
}

.manage-button {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.12);
}

.header-buttons {
  display: flex;
  gap: 10px;
}

.header-list-button,
.header-back-button {
  font-size: 16PX;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.12);

  &:hover,
  &:focus {
    color: #fff;
    border-color: #fff;
    background: rgba(255, 255, 255, 0.2);
  }
}

.step-wrapper {
  padding: 20PX 44PX 14PX;
  border-bottom: 1PX solid #ebeef5;
  background: #f7f9fa;

  ::v-deep .clickable-step {
    cursor: pointer;

    .el-step__head,
    .el-step__main,
    .el-step__icon {
      cursor: pointer;
    }

    &:hover .el-step__title {
      color: #20a464;
    }
  }
}

.employee-form {
  background: #fff;
}

.step-content {
  padding: 0 32PX 28PX;
}

.section-title {
  display: flex;
  align-items: center;
  height: 58PX;
  margin-bottom: 24PX;
  border-bottom: 2PX solid #20a464;
  font-size: 20PX;

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30PX;
    height: 30PX;
    margin-right: 10PX;
    border-radius: 6PX;
    color: #fff;
    background: #20a464;
  }

  em {
    margin-left: 12PX;
    font-size: 15PX;
    font-style: normal;
    color: #f04455;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 26PX;

  .full-row {
    grid-column: 1 / -1;
  }
}

/* 省市区级联 + 详细地址 横向排列 */
.address-row {
  display: flex;
  align-items: center;
  gap: 10PX;
  width: 100%;

  /* 省市区选择触发器：模拟输入框外观，点击弹出底部选择器 */
  .address-area {
    flex: 0 0 46%;
    min-width: 0;
    display: flex;
    align-items: center;
    height: 36PX;
    padding: 0 10PX;
    border: 1PX solid #dcdfe6;
    border-radius: 6PX;
    background: #fff;
    cursor: pointer;
    box-sizing: border-box;
    font-size: 14PX;
    color: #333;

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.is-placeholder {
        color: #c0c4cc;
      }
    }

    &__icon {
      margin-left: 6PX;
      color: #c0c4cc;
      font-size: 14PX;
    }

    &:active {
      background: #f5f7fa;
    }
  }

  .address-detail {
    flex: 1;
    min-width: 0;
  }
}

.repeat-card {
  margin-bottom: 18PX;
  padding: 18PX 20PX 2PX;
  border: 1PX solid #dfe5e9;
  border-radius: 12PX;
  background: #fff;
}

.repeat-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12PX;

  strong {
    font-size: 17PX;
    color: #16985a;
  }
}

.add-button {
  min-width: 165PX;
  height: 44PX;
  margin-top: 2PX;
  border-radius: 8PX;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24PX 32PX;
  border-top: 1PX solid #ebeef5;

  .previous-button,
  .next-button {
    min-width: 148PX;
    height: 48PX;
    border-radius: 9PX;
    font-size: 17PX;
    font-weight: 600;
  }

  .previous-button {
    color: #fff;
    border-color: #7c8790;
    background: #7c8790;
  }
}

.confirm-content {
  min-height: 510PX;
}

.summary-card {
  margin-top: 20PX;
  padding: 20PX;
  border: 1PX solid #e1e7ea;
  border-radius: 10PX;

  h3 {
    margin: 0 0 16PX;
    color: #16985a;
    font-size: 17PX;
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16PX 24PX;

  div {
    font-size: 15PX;
  }

  span {
    display: block;
    margin-bottom: 6PX;
    color: #909399;
    font-size: 13PX;
  }
}

.summary-count {
  display: flex;
  flex-wrap: wrap;
  gap: 16PX 36PX;
  font-size: 15PX;
}

.confirm-checkbox {
  margin-top: 24PX;
}

.submit-success-overlay {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24PX;
  background: rgba(19, 34, 27, 0.46);
  box-sizing: border-box;
}

.submit-success-card {
  width: 100%;
  max-width: 620PX;
  padding: 54PX 48PX 50PX;
  text-align: center;
  background: #fff;
  border-radius: 18PX;
  box-shadow: 0 24PX 70PX rgba(15, 54, 35, 0.22);
  box-sizing: border-box;

  h2 {
    margin: 26PX 0 12PX;
    font-size: 32PX;
    font-weight: 700;
    color: #18a45e;
  }

  p {
    margin: 0;
    font-size: 20PX;
    color: #7a8790;
  }
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 94PX;
  height: 94PX;
  margin: 0 auto;
  font-size: 56PX;
  font-weight: 700;
  color: #fff;
  background: #20a464;
  border-radius: 50%;
  box-shadow: 0 12PX 28PX rgba(32, 164, 100, 0.24);
}

.success-actions {
  display: flex;
  justify-content: center;
  gap: 18PX;
  margin-top: 34PX;
}

::v-deep .success-actions .el-button {
  min-width: 180PX;
  height: 56PX;
  margin: 0;
  border-radius: 9PX;
  font-size: 18PX;
  font-weight: 600;
}

::v-deep .success-secondary-button {
  color: #18a45e;
  border: 2PX solid #20a464;
  background: #fff;
}

::v-deep .success-primary-button {
  border-color: #20a464;
  background: #20a464;
}

.success-fade-enter-active,
.success-fade-leave-active {
  transition: opacity 0.2s ease;

  .submit-success-card {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
}

.success-fade-enter,
.success-fade-leave-to {
  opacity: 0;

  .submit-success-card {
    opacity: 0;
    transform: translateY(18PX) scale(0.96);
  }
}

::v-deep .el-form-item {
  margin-bottom: 20PX;
}

::v-deep .el-form-item__label {
  padding: 0 0 7PX;
  color: #4d5962;
  font-size: 15PX;
  line-height: 20PX;
}

::v-deep .el-input,
::v-deep .el-select,
::v-deep .el-date-editor,
::v-deep .el-cascader {
  width: 100%;
}

::v-deep .el-input__inner {
  height: 42PX;
  line-height: 42PX;
  border-radius: 7PX;
  font-size: 14PX;
}

::v-deep .el-textarea__inner {
  border-radius: 7PX;
  font-size: 14PX;
}

::v-deep .el-form-item.is-required:not(.is-no-asterisk)
  > .el-form-item__label::before {
  color: #e5394f;
}

::v-deep .el-step__head.is-process,
::v-deep .el-step__title.is-process,
::v-deep .el-step__head.is-finish,
::v-deep .el-step__title.is-finish,
::v-deep .el-step__head.is-success,
::v-deep .el-step__title.is-success {
  color: #1fa463;
  border-color: #1fa463;
}

@media (max-width: 768PX) {
  .employee-page {
    padding: 0;
  }

  .page-header {
    min-height: 92PX;
    padding: 16PX 18PX;

    h1 {
      font-size: 20PX;
    }

    p {
      font-size: 12PX;
    }
  }

  .manage-button {
    padding: 8PX;

    i {
      margin-right: 0;
    }
  }

  .step-wrapper {
    padding: 18PX 8PX 12PX;
  }

  .step-content {
    padding: 0 15PX 22PX;
  }

  .section-title {
    margin-bottom: 18PX;
    font-size: 18PX;
  }

  .form-grid,
  .summary-grid {
    grid-template-columns: 1fr;
    column-gap: 0;
  }

  .repeat-card {
    padding: 15PX 14PX 0;
  }

  .form-actions {
    padding: 18PX 15PX;

    .previous-button,
    .next-button {
      min-width: 126PX;
    }
  }

  .submit-success-overlay {
    padding: 18PX;
  }

  .submit-success-card {
    padding: 42PX 20PX 34PX;

    h2 {
      margin-top: 22PX;
      font-size: 28PX;
    }

    p {
      font-size: 17PX;
    }
  }

  .success-icon {
    width: 82PX;
    height: 82PX;
    font-size: 48PX;
  }

  .success-actions {
    flex-direction: column;
    gap: 12PX;
    margin-top: 30PX;
  }

  ::v-deep .success-actions .el-button {
    width: 100%;
    min-width: 0;
  }

  ::v-deep .el-step__title {
    font-size: 12PX;
  }
}
</style>
